import { Component, inject, OnInit, OnDestroy, signal, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { EditorModule } from 'primeng/editor';
import { Subject, takeUntil, forkJoin, firstValueFrom } from 'rxjs';

import { AssessmentsService } from '../../service/entity-services/assessments.service';
import { UsersService } from '../../service/entity-services/users.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { PermissionsStoreService } from '../../service/permissions/permissions-store.service';
import { EntityActionsSidenavComponent, EntityAction } from '../../entity-form-components/entity-actions-sidenav/entity-actions-sidenav.component';
import { Assessment, AssessmentPerson, AssessmentScoreChoice } from '../../objects/assessment';

type PersonField = 'owner' | 'assessor' | 'finalReviewer';

/**
 * Create / edit an assessment. Modeled on the legacy SRH form (new-risk-assessment on
 * prod-srh) so Self Regional users see the same fields: Name, Type, Owner / Assessor /
 * Final Reviewer, Critical Date, Status, Quality Metrics, scored questions, Total Score,
 * Recommendations, Evaluation Instructions and the attestation.
 *
 * Saving (v2 API) sends the COMPLETE record — the loaded record with the form's fields laid
 * over it — because PUT is a full replace. Fields this form doesn't show (contracts, notes,
 * versionList, …) are carried through untouched.
 */
@Component({
  selector: 'app-new-assessment',
  templateUrl: './new-assessment.component.html',
  styleUrls: ['./new-assessment.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDividerModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatSnackBarModule,
    MatAutocompleteModule,
    EditorModule,
    EntityActionsSidenavComponent
  ]
})
export class NewAssessmentComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private assessmentsService = inject(AssessmentsService);
  private usersService = inject(UsersService);
  private sidenavContentService = inject(SidenavContentService);
  private permissionsStore = inject(PermissionsStoreService);
  private snackBar = inject(MatSnackBar);
  private destroy$ = new Subject<void>();

  @ViewChild('actionsSidenav') actionsSidenav!: MatSidenav;

  form!: FormGroup;
  loading = true;
  saving = false;
  isEditMode = false;
  loadError: string | null = null;
  saveError: string | null = null;
  /** True after a 409: the record changed under us; the only way forward is a reload. */
  conflict = false;

  /** The record as loaded (edit mode). Saves lay the form over this. */
  private loadedRecord: Assessment | null = null;
  private loadedRevision = 0;

  readonly isViewer = this.permissionsStore.isViewer;

  // Sheet data
  assessmentTypes: string[] = [];
  assessmentStatuses: string[] = [];
  scoreChoices: AssessmentScoreChoice[] = [];
  attestationText = '';
  instructionsHtml = '';

  // People
  private allPeople: AssessmentPerson[] = [];
  filteredPeople: { [K in PersonField]: AssessmentPerson[] } = { owner: [], assessor: [], finalReviewer: [] };

  // Source entity context (create mode, from the record's Assessments tab)
  sourceEntityType = '';
  sourceEntityId = '';
  sourceRecord = '';

  sidenavActions = signal<EntityAction[]>([
    { label: 'Save', icon: 'save', action: () => this.onSubmit(), disabled: false },
    { label: 'Cancel', icon: 'close', action: () => this.goBack(), disabled: false }
  ]);

  ngOnInit(): void {
    this.initForm();

    this.sidenavContentService.registerDetailSidenavToggle(() => {
      if (this.actionsSidenav) this.actionsSidenav.toggle();
    });

    this.usersService.allUsers$.pipe(takeUntil(this.destroy$)).subscribe(users => {
      this.allPeople = (users || [])
        .filter((u: any) => u?.email)
        .map((u: any) => this.toPerson(u))
        .sort((a, b) => (a.shortFriendlyName || '').localeCompare(b.shortFriendlyName || ''));
    });

    const qp = this.route.snapshot.queryParamMap;
    this.sourceEntityType = qp.get('entityType') || '';
    this.sourceEntityId = qp.get('entityId') || '';
    this.sourceRecord = qp.get('sourceRecord') || '';

    const id = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!id;
    this.load(id);
  }

  ngOnDestroy(): void {
    this.sidenavContentService.unregisterDetailSidenavToggle();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', Validators.required],
      type: [''],
      status: ['Pending Completion'],
      owner: [null],
      assessor: [null],
      finalReviewer: [null],
      criticalDate: [null],
      qualityMetricsLocation: [''],
      qualityMetricsLanguageExcerpt: [''],
      recommendation: [''],
      questionsAndAnswers: this.fb.array([])
    });
  }

  private async load(id: string | null): Promise<void> {
    this.loading = true;
    this.loadError = null;
    try {
      const sheet = await firstValueFrom(forkJoin({
        types: this.assessmentsService.getAssessmentTypes(),
        statuses: this.assessmentsService.getAssessmentStatuses(),
        scores: this.assessmentsService.getScoreChoices(),
        attestation: this.assessmentsService.getAttestation(),
        instructions: this.assessmentsService.getEvaluationInstructions()
      }));
      this.assessmentTypes = sheet.types.map(t => (t || '').trim()).filter(t => t);
      this.assessmentStatuses = sheet.statuses.map(s => (s || '').trim()).filter(s => s);
      this.scoreChoices = sheet.scores;
      this.attestationText = sheet.attestation?.[0] || '';
      this.instructionsHtml = sheet.instructions;

      if (id) {
        const record = await firstValueFrom(this.assessmentsService.usesV2
          ? this.assessmentsService.getAssessment(id)
          : this.assessmentsService.getAssessmentByNum(id));
        if (!record) {
          this.loadError = 'Assessment not found.';
        } else {
          await this.populateForm(record);
        }
      } else if (this.assessmentTypes.length === 1) {
        // One type (SRH today): pick it and load its questions.
        this.form.patchValue({ type: this.assessmentTypes[0] });
        await this.loadQuestions(this.assessmentTypes[0]);
      }
    } catch (err) {
      this.loadError = this.assessmentsService.describeError(err);
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private async populateForm(record: Assessment): Promise<void> {
    this.loadedRecord = record;
    this.loadedRevision = record.revision ?? 0;
    this.sourceRecord = record.sourceRecord || '';

    // Keep a status that isn't in the sheet list (older data) selectable.
    if (record.status && !this.assessmentStatuses.includes(record.status)) {
      this.assessmentStatuses = [record.status, ...this.assessmentStatuses];
    }

    this.form.patchValue({
      name: record.name || '',
      type: record.type || '',
      status: record.status || '',
      owner: this.resolvePerson(record.owner),
      assessor: this.resolvePerson(record.assessor),
      finalReviewer: this.resolvePerson(record.finalReviewer),
      criticalDate: record.criticalDate ? new Date(record.criticalDate) : null,
      qualityMetricsLocation: record.qualityMetricsLocation || '',
      qualityMetricsLanguageExcerpt: record.qualityMetricsLanguageExcerpt || '',
      recommendation: record.recommendation || ''
    });
    // Type is fixed once an assessment exists (legacy rule): its questions belong to it.
    this.form.get('type')?.disable();

    const qa = record.questionsAndAnswers || [];
    if (qa.length > 0) {
      this.setQuestions(qa.map(q => ({ question: q.question, score: this.toScore(q.score), comment: q.comment || '' })));
    } else if (record.type) {
      await this.loadQuestions(record.type);
    }
  }

  /** Type picked on a new assessment: replace the questions with that type's set. */
  async onTypeSelected(type: string): Promise<void> {
    await this.loadQuestions(type);
    this.cdr.detectChanges();
  }

  private async loadQuestions(type: string): Promise<void> {
    const questions = await firstValueFrom(this.assessmentsService.getQuestionsForType(type, this.assessmentTypes));
    // New questions start at 0 = "NA - Unable to Rate", as in the legacy form.
    this.setQuestions(questions.map(question => ({ question, score: 0, comment: '' })));
  }

  private setQuestions(rows: { question: string; score: number; comment: string }[]): void {
    const arr = this.questionsFormArray;
    arr.clear();
    rows.forEach(r => arr.push(this.fb.group({ question: [r.question], score: [r.score], comment: [r.comment] })));
  }

  get questionsFormArray(): FormArray {
    return this.form.get('questionsAndAnswers') as FormArray;
  }

  get totalScore(): number {
    return this.assessmentsService.sumScores(this.questionsFormArray.value);
  }

  private toScore(score: number | string | undefined): number {
    const n = Number(score);
    return isNaN(n) ? 0 : n;
  }

  /** Text for a score; falls back to the bare number if the sheet has no matching choice. */
  private scoreDescription(score: number): string {
    return this.scoreChoices.find(c => c.score === score)?.description ?? String(score);
  }

  // ── People pickers ──────────────────────────────────────────────

  private toPerson(u: any): AssessmentPerson {
    const person: AssessmentPerson = {
      email: u.email,
      given_name: u.given_name ?? u.firstName ?? '',
      family_name: u.family_name ?? u.lastName ?? '',
      shortFriendlyName: u.shortFriendlyName || u.email,
      friendlyName: u.friendlyName || u.email
    };
    if (u.title) person.title = u.title;
    if (u.userId) person.userId = u.userId;
    return person;
  }

  /** Stored value → person object. Old records may hold a bare email. */
  private resolvePerson(value: any): AssessmentPerson | null {
    if (!value) return null;
    if (typeof value === 'object') return value.email ? value : null;
    const email = String(value).toLowerCase().trim();
    return this.allPeople.find(p => p.email.toLowerCase() === email) || { email, shortFriendlyName: email, friendlyName: email };
  }

  filterPeople(field: PersonField, text: any): void {
    const q = (typeof text === 'string' ? text : '').toLowerCase().trim();
    this.filteredPeople[field] = (q
      ? this.allPeople.filter(p => (p.friendlyName || '').toLowerCase().includes(q))
      : this.allPeople).slice(0, 50);
  }

  displayPerson = (p: AssessmentPerson | string | null): string => {
    if (!p) return '';
    if (typeof p === 'string') return p;
    return p.friendlyName || p.shortFriendlyName || p.email;
  };

  /** Typed text that wasn't picked from the list is not a person — clear it on blur. */
  onPersonBlur(field: PersonField): void {
    const v = this.form.get(field)?.value;
    if (typeof v === 'string') this.form.get(field)?.setValue(null);
  }

  // ── Save ────────────────────────────────────────────────────────

  async onSubmit(): Promise<void> {
    if (this.saving || this.conflict) return;
    if (this.isViewer()) {
      this.saveError = 'Viewers can look at assessments but not change them.';
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.saveError = 'Please enter a name for the assessment.';
      return;
    }
    this.saving = true;
    this.saveError = null;

    const v = this.form.getRawValue();
    const questionsAndAnswers = (v.questionsAndAnswers as { question: string; score: number; comment: string }[])
      .map(q => ({ question: q.question, score: q.score, scoreDescription: this.scoreDescription(q.score), comment: q.comment || '' }));

    const fromForm: Assessment = {
      name: v.name,
      type: v.type,
      status: v.status,
      owner: v.owner || null,
      assessor: v.assessor || null,
      finalReviewer: v.finalReviewer || null,
      criticalDate: v.criticalDate ? new Date(v.criticalDate).toISOString() : null,
      qualityMetricsLocation: v.qualityMetricsLocation || '',
      qualityMetricsLanguageExcerpt: v.qualityMetricsLanguageExcerpt || '',
      recommendation: v.recommendation || '',
      questionsAndAnswers,
      totalScore: this.assessmentsService.sumScores(questionsAndAnswers)
    };

    try {
      let saved: Assessment;
      if (!this.assessmentsService.usesV2) {
        saved = await this.saveLegacy(fromForm);
      } else if (this.loadedRecord) {
        saved = await firstValueFrom(this.assessmentsService.replaceAssessment(
          { ...this.loadedRecord, ...fromForm }, this.loadedRevision));
      } else {
        saved = await firstValueFrom(this.assessmentsService.createAssessment({
          ...fromForm,
          entityType: this.sourceEntityType.toLowerCase() as any,
          entityId: this.sourceEntityId,
          sourceRecord: this.sourceRecord
        }));
      }
      this.snackBar.open('Assessment saved', 'Close', { duration: 3000 });
      this.router.navigate(['/securehome/assessment', saved.assessmentNum], { replaceUrl: true });
    } catch (err: any) {
      this.conflict = err?.status === 409;
      this.saveError = this.assessmentsService.describeError(err);
    } finally {
      this.saving = false;
      this.cdr.detectChanges();
    }
  }

  /** Orgs still on the legacy API: unchanged behavior (client-made id, write-full). */
  private async saveLegacy(fromForm: Assessment): Promise<Assessment> {
    const record: Assessment = {
      ...(this.loadedRecord || {
        entityType: this.sourceEntityType.toLowerCase() as any,
        entityId: this.sourceEntityId,
        sourceRecord: this.sourceRecord
      }),
      ...fromForm,
      assessmentNum: this.loadedRecord?.assessmentNum || Date.now().toString()
    };
    await firstValueFrom(this.assessmentsService.writeAssessment(record));
    return record;
  }

  /** After a conflict: throw away this edit and load what's saved now. */
  reload(): void {
    this.conflict = false;
    this.saveError = null;
    this.load(this.loadedRecord?.assessmentNum || null);
  }

  goBack(): void {
    if (this.isEditMode && this.loadedRecord?.assessmentNum) {
      this.router.navigate(['/securehome/assessment', this.loadedRecord.assessmentNum]);
    } else if (this.sourceEntityId) {
      window.history.back();
    } else {
      this.router.navigate(['/securehome/assessments']);
    }
  }
}
